/**
 * Customer Hooks - T067
 *
 * TanStack Query hooks for customer CRUD operations
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api';
import type {
  CreateCustomerInput,
  UpdateCustomerInput,
  CustomerListQuery as CustomerListQueryType,
  CreateAddressInput,
  UpdateAddressInput,
} from '@armard/shared';

// Re-export for convenience
export type CustomerListQuery = Partial<CustomerListQueryType>;

// ─────────────────────────────────────────────────────────────
// TYPES
// ─────────────────────────────────────────────────────────────

export interface Customer {
  id: string;
  customerNumber: string;
  companyName: string;
  contactFirstName: string | null;
  contactLastName: string | null;
  email: string | null;
  phone: string | null;
  defaultPaymentDays: number;
  notes: string | null;
  isArchived: boolean;
  createdAt: string;
  updatedAt: string;
  version: number;
}

export interface Address {
  id: string;
  customerId: string;
  addressType: 'billing' | 'shipping';
  isDefault: boolean;
  street: string;
  street2: string | null;
  postalCode: string;
  city: string;
  country: string;
  createdAt: string;
  updatedAt: string;
}

export interface CustomerListResult {
  customers: Customer[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

// ─────────────────────────────────────────────────────────────
// QUERY KEYS
// ─────────────────────────────────────────────────────────────

export const customerKeys = {
  all: ['customers'] as const,
  lists: () => [...customerKeys.all, 'list'] as const,
  list: (query: CustomerListQuery) => [...customerKeys.lists(), query] as const,
  details: () => [...customerKeys.all, 'detail'] as const,
  detail: (id: string) => [...customerKeys.details(), id] as const,
  addresses: (customerId: string) => [...customerKeys.detail(customerId), 'addresses'] as const,
  search: (q: string) => [...customerKeys.all, 'search', q] as const,
};

// ─────────────────────────────────────────────────────────────
// CUSTOMER HOOKS
// ─────────────────────────────────────────────────────────────

/**
 * Hook to list customers with pagination and filtering
 */
export function useCustomerList(query: CustomerListQuery = {}) {
  const fullQuery = {
    page: query.page ?? 1,
    limit: query.limit ?? 40,
    ...query,
  };

  return useQuery({
    queryKey: customerKeys.list(fullQuery),
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set('page', String(fullQuery.page));
      params.set('limit', String(fullQuery.limit));
      if (fullQuery.q) params.set('q', fullQuery.q);
      if (fullQuery.isArchived !== undefined) params.set('isArchived', String(fullQuery.isArchived));
      if (fullQuery.sortBy) params.set('sortBy', fullQuery.sortBy);
      if (fullQuery.sortOrder) params.set('sortOrder', fullQuery.sortOrder);

      return api.get<CustomerListResult>(`/customers?${params.toString()}`);
    },
  });
}

/**
 * Hook to get a single customer by ID
 */
export function useCustomer(id: string) {
  return useQuery({
    queryKey: customerKeys.detail(id),
    queryFn: () => api.get<Customer>(`/customers/${id}`),
    enabled: !!id,
  });
}

/**
 * Hook to search customers (for autocomplete)
 */
export function useCustomerSearch(q: string, limit = 10) {
  return useQuery({
    queryKey: customerKeys.search(q),
    queryFn: () => api.get<Customer[]>(`/customers/search?q=${encodeURIComponent(q)}&limit=${limit}`),
    enabled: q.length >= 2,
  });
}

/**
 * Hook to create a customer
 */
export function useCreateCustomer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateCustomerInput) => api.post<Customer>('/customers', input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: customerKeys.lists() });
    },
  });
}

/**
 * Hook to update a customer
 */
export function useUpdateCustomer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...input }: UpdateCustomerInput & { id: string }) =>
      api.patch<Customer>(`/customers/${id}`, input),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: customerKeys.lists() });
      queryClient.setQueryData(customerKeys.detail(data.id), data);
    },
  });
}

/**
 * Hook to archive/unarchive a customer
 */
export function useArchiveCustomer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, isArchived, version }: { id: string; isArchived: boolean; version: number }) =>
      api.patch<Customer>(`/customers/${id}/archive`, { isArchived, version }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: customerKeys.lists() });
      queryClient.setQueryData(customerKeys.detail(data.id), data);
    },
  });
}

// ─────────────────────────────────────────────────────────────
// ADDRESS HOOKS
// ─────────────────────────────────────────────────────────────

/**
 * Hook to list addresses for a customer
 */
export function useCustomerAddresses(customerId: string) {
  return useQuery({
    queryKey: customerKeys.addresses(customerId),
    queryFn: () => api.get<Address[]>(`/customers/${customerId}/addresses`),
    enabled: !!customerId,
  });
}

/**
 * Hook to create an address
 */
export function useCreateAddress(customerId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateAddressInput) =>
      api.post<Address>(`/customers/${customerId}/addresses`, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: customerKeys.addresses(customerId) });
    },
  });
}

/**
 * Hook to update an address
 */
export function useUpdateAddress(customerId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ addressId, ...input }: UpdateAddressInput & { addressId: string }) =>
      api.patch<Address>(`/customers/${customerId}/addresses/${addressId}`, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: customerKeys.addresses(customerId) });
    },
  });
}

/**
 * Hook to delete an address
 */
export function useDeleteAddress(customerId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (addressId: string) =>
      api.delete(`/customers/${customerId}/addresses/${addressId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: customerKeys.addresses(customerId) });
    },
  });
}
